import { RuleSeverity } from 'types/dt-graphql-apollo-v2';
import {
  ConditionCheckView,
  ConditionOperatorLabel,
  conditionOperatorNeedsValue,
  ConsistencyRuleView,
  RuleOperatorLabel,
} from './types';

export const DISABLED_TAG = '_disabled';

// Tag-level disabled check — works on raw API tags or a view's tags. The shared
// GetConsistencyCollections query no longer filters disabled rules out, so the
// rule list shows them (with a "Disabled" label) while report mappers drop them.
export const isRuleDisabled = (
  tags: ReadonlyArray<string | null> | null | undefined
): boolean => (tags ?? []).includes(DISABLED_TAG);

export const isConsistencyRuleDisabled = (rule: ConsistencyRuleView): boolean =>
  isRuleDisabled(rule.tags);

export const getDisabledConsistencyRulesCount = (
  rules: ConsistencyRuleView[]
): number => rules.filter(isConsistencyRuleDisabled).length;

export const toggleDisabledTag = (
  tags: (string | null)[] | null | undefined,
  disable: boolean
): string[] => {
  const clean = (tags ?? []).filter(
    (t): t is string => typeof t === 'string' && t !== DISABLED_TAG
  );
  return disable ? [...clean, DISABLED_TAG] : clean;
};

export const formatConditionCheckExpression = (
  check: ConditionCheckView,
  sourceSchemaName: string,
  targetSchemaName: string
): string => {
  const attributeSchemaName =
    check.isLiteralValue && check.useTargetSchema
      ? targetSchemaName
      : sourceSchemaName;
  const sourcePrefix = check.sourceAttribute ? `[${attributeSchemaName}].` : '';
  const operatorLabel =
    ConditionOperatorLabel[check.operator] ?? check.operator;
  const head = `${sourcePrefix}${check.sourceAttribute} ${operatorLabel}`;

  if (!conditionOperatorNeedsValue(check.operator) || !check.targetAttribute) {
    return head;
  }

  const valueSuffix = check.isLiteralValue
    ? ` "${check.targetAttribute}"`
    : ` [${targetSchemaName}].${check.targetAttribute}`;
  return `${head}${valueSuffix}`;
};

export type RuleSortColumn =
  | 'customerDefinedId'
  | 'name'
  | 'operator'
  | 'severity';

export type RuleSortDirection = 'asc' | 'desc';

export type RuleSort = {
  column: RuleSortColumn;
  direction: RuleSortDirection;
};

export const SEVERITY_RANK: Record<RuleSeverity, number> = {
  [RuleSeverity.Critical]: 4,
  [RuleSeverity.Warning]: 3,
  [RuleSeverity.Minor]: 2,
  [RuleSeverity.None]: 1,
};

const compareNullableStrings = (
  a: string | null,
  b: string | null,
  direction: RuleSortDirection,
  options?: Intl.CollatorOptions
): number => {
  // Nulls always sink to the bottom, regardless of direction.
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  const cmp = a.localeCompare(b, undefined, options);
  return direction === 'asc' ? cmp : -cmp;
};

export const sortConsistencyRules = (
  rules: ConsistencyRuleView[],
  sort: RuleSort | null
): ConsistencyRuleView[] => {
  if (sort === null) return rules;

  const copy = [...rules];
  const dir = sort.direction;

  switch (sort.column) {
    case 'customerDefinedId':
      copy.sort((a, b) =>
        compareNullableStrings(a.customerDefinedId, b.customerDefinedId, dir, {
          numeric: true,
          sensitivity: 'base',
        })
      );
      break;
    case 'name':
      copy.sort((a, b) => compareNullableStrings(a.name, b.name, dir));
      break;
    case 'operator':
      copy.sort((a, b) => {
        const labelA = RuleOperatorLabel[a.operator] ?? a.operator;
        const labelB = RuleOperatorLabel[b.operator] ?? b.operator;
        return compareNullableStrings(labelA, labelB, dir);
      });
      break;
    case 'severity':
      copy.sort((a, b) => {
        const rankA = SEVERITY_RANK[a.severity];
        const rankB = SEVERITY_RANK[b.severity];
        return dir === 'asc' ? rankA - rankB : rankB - rankA;
      });
      break;
  }

  return copy;
};
